import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { createContext, memo, useMemo } from "react";
import type { DraftCollectionItem } from "../db/collections";
import { useGlobalSupportTicketCollections } from "./global-support-ticket-collections-context";

export interface SupportTicketDraftsContextValue {
  drafts: DraftCollectionItem[];
  isLoading: boolean;
}

export const SupportTicketDraftsContext = createContext<
  SupportTicketDraftsContextValue | undefined
>(undefined);
SupportTicketDraftsContext.displayName = "SupportTicketDraftsContext";

export function useSupportTicketDrafts(): SupportTicketDraftsContextValue {
  return useRequiredContext(SupportTicketDraftsContext);
}

interface SupportTicketDraftsProviderProps {
  supportTicketId: SupportTicketId;
  hasDraft: boolean;
  children: React.ReactNode;
}

/**
 * Provider that fetches drafts for a conversation once at the conversation level.
 * Uses the `hasDraft` field from the OpenSearch index to skip fetching when
 * there are no drafts, avoiding unnecessary API calls.
 */
export const SupportTicketDraftsProvider = memo(
  function SupportTicketDraftsProvider({
    supportTicketId,
    hasDraft,
    children,
  }: SupportTicketDraftsProviderProps) {
    const collections = useGlobalSupportTicketCollections();

    // Only query drafts if the conversation has drafts according to OpenSearch
    // Return null to skip query when hasDraft is false
    const result = useLiveQuery(
      (q) =>
        hasDraft
          ? q
              .from({ draft: collections.drafts })
              .where(({ draft }) =>
                eq(draft.supportTicketId, String(supportTicketId)),
              )
          : null,
      [supportTicketId, hasDraft],
    );

    const value = useMemo(
      (): SupportTicketDraftsContextValue => ({
        drafts: hasDraft && result.data ? result.data : [],
        isLoading: hasDraft ? result.isLoading : false,
      }),
      [hasDraft, result.data, result.isLoading],
    );

    return (
      <SupportTicketDraftsContext.Provider value={value}>
        {children}
      </SupportTicketDraftsContext.Provider>
    );
  },
);
